/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import type { LearnerProfile_ProfilePhoto as ProfilePhoto } from '__generated__/graphql-types-do-not-use';

import { Button, Typography2 } from '@coursera/cds-core';

import PhotoUploadButton from 'bundles/account-profile/components/common/PhotoUploadButton';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileImage from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfileImage';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const PROFILE_PHOTO_SIZE = 200;

const styles = {
  container: () => css`
    margin-bottom: var(--cds-spacing-300);
  `,
  label: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,

  buttonsContainer: () => css`
    display: flex;
    margin-bottom: var(--cds-spacing-200);
  `,

  removeButton: () => css`
    margin-left: var(--cds-spacing-200);
  `,

  progressBarContainer: () => css`
    border: 1px solid var(--cds-color-grey-50);
    padding: 2px;
    margin-bottom: var(--cds-spacing-100);
    border-radius: 4px;
  `,
  progressBar: () => css`
    position: relative;
    background-color: var(--cds-color-blue-700);
    height: 10px;
    border-radius: 2px;
  `,
  errorMsg: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

export type Props = {
  onChange: (imgs?: ProfilePhoto) => void;
  value?: ProfilePhoto | null;
};

const ProfilePhotoUploader = ({ value, onChange }: Props) => {
  const { profileState } = useContext(ProfileContext);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [profileImage, setProfileImage] = useState<string | null | undefined>(value?.default);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onUpload = () => {
    setIsUploading(true);
    setErrorMessage(null);
  };

  const onProgress = (currentProgress: number) => {
    setProgress(currentProgress);
  };

  const onSuccess = (imgs: ProfilePhoto) => {
    setIsUploading(false);
    setProfileImage(imgs.default);
    onChange(imgs);
  };

  const onError = (error: string) => {
    setErrorMessage(error);
    setIsUploading(false);
  };

  const removePhoto = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    const emptyImage = { size120: '', size60: '', size24: '', default: '' };
    setProfileImage(null);
    setErrorMessage(null);
    onChange(emptyImage);
  };

  let label = profileImage ? _t('Change photo') : _t('Upload photo');
  if (isUploading) label = _t('Uploading...');

  return (
    <div css={styles.container} id="coursera-profile-photo-form">
      <Typography2 css={styles.label} component="label" htmlFor="photo-uploader" variant="subtitleMedium">
        {_t('Profile Photo')}
      </Typography2>
      <PhotoUploadButton
        label={label}
        onUpload={onUpload}
        onProgress={onProgress}
        onSuccess={onSuccess}
        onError={onError}
        disabled={isUploading}
        render={(onClick) => (
          <ProfileImage
            onClick={onClick}
            size={PROFILE_PHOTO_SIZE}
            image={profileImage || undefined}
            fullName={profileState?.fullName || ''}
          />
        )}
      />
      <div css={styles.buttonsContainer}>
        <div aria-live="polite">
          <PhotoUploadButton
            label={label}
            onUpload={onUpload}
            onProgress={onProgress}
            onSuccess={onSuccess}
            onError={onError}
            disabled={isUploading}
          />
        </div>
        {!!profileImage && (
          <Button
            // not tracked in eventing v3
            trackingName="profile_image_remove_button"
            withVisibilityTracking={false}
            requireFullyVisible={false}
            component={TrackedButton}
            size="small"
            variant="secondary"
            onClick={removePhoto}
            aria-label={_t('Remove photo')}
            disabled={isUploading}
            css={styles.removeButton}
          >
            {_t('Remove photo')}
          </Button>
        )}
      </div>
      {isUploading && (
        <div css={styles.progressBarContainer}>
          <div css={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>
      )}
      {!!errorMessage && (
        <Typography2 component="p" css={styles.errorMsg} color="error">
          {errorMessage}
        </Typography2>
      )}
      <Typography2 component="p">{_t('Maximum size: 1MB. Supported formats: JPG, GIF, or PNG.')}</Typography2>
    </div>
  );
};

export default ProfilePhotoUploader;
